import React, { MutableRefObject, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';

import Modal from '~/App/Components/Modal';
import Checkbox from '~/App/Components/Checkbox';
import { ReportReason, ReportReasons } from '~/types/models/report';
import type { ReportResponse } from '~/App/Api/Report';
import { AxiosResponse } from 'axios';

const StyledModal = styled(Modal)`
  max-width: unset;
  width: 400px !important;

  > div {
    padding: 40px;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 600px) {
    width: 90vw !important;
  }
`;

const Reason = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`;

const ReasonsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OtherReasonInput = styled.input`
  width: 100%;
`;

const Title = styled.div`
  font-size: 20px;
  margin-bottom: 25px;
`;

const SubmitConfirmation = styled.div`
  font-size: 20px;
  align-self: center;
  width: 200px;
  text-align: center;
`;

const SendReport = styled(Button)`
  width: auto;
  align-self: flex-end;
`;

const OtherReason = styled.div`
  height: 45px;
`;

export interface ReportModalProps {
  showModal: boolean;
  onClose: () => void;
  onSubmit: (
    reasonType: string,
    reasonText?: string
  ) => Promise<AxiosResponse<ReportResponse>>;
}

export default function ReportModal({
  showModal,
  onSubmit,
  onClose,
}: ReportModalProps) {
  const [reason, setReason] = useState('');
  const [isOtherReason, setIsOtherReason] = useState(false);
  const [otherReason, setOtherReason] = useState<string>();
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);

  const otherReasonInputRef = useRef<HTMLInputElement>();

  function submitReport() {
    onSubmit(reason, otherReason).catch(() => setSubmissionFailed(true));
    setReportSubmitted(true);
  }

  function isValidReport() {
    return !(!reason || (reason === ReportReason.Other && !otherReason));
  }

  function onReasonChange(newReason: string) {
    setIsOtherReason(false);
    if (newReason === reason) {
      setReason('');
      return;
    }

    if (newReason === ReportReason.Other) {
      setIsOtherReason(true);
    }

    setReason(newReason);
  }

  if (reportSubmitted) {
    return (
      <StyledModal isOpen={showModal} onRequestClose={onClose} hideActions>
        <SubmitConfirmation>
          {!submissionFailed ? 'Thank you!' : 'Something went wrong.'}
        </SubmitConfirmation>
        <SubmitConfirmation>
          Your report {!submissionFailed ? 'has been' : 'is not successfully'}{' '}
          submitted
        </SubmitConfirmation>
        {submissionFailed && (
          <SubmitConfirmation>Please try again.</SubmitConfirmation>
        )}

        <Button
          bsStyle={submissionFailed ? 'danger' : 'primary'}
          style={{ width: 70, alignSelf: 'center', marginTop: 30 }}
          onClick={onClose}
        >
          Close
        </Button>
      </StyledModal>
    );
  }
  return (
    <StyledModal isOpen={showModal} onRequestClose={onClose} hideActions>
      <Title>Report this page</Title>
      <ReasonsContainer>
        {Object.entries(ReportReasons).map(([key, value]) => {
          return (
            <Reason key={key}>
              <Checkbox
                name={key}
                checked={reason === key}
                onChange={() => onReasonChange(key)}
                label={value}
              />
            </Reason>
          );
        })}

        <OtherReason>
          {isOtherReason && (
            <OtherReasonInput
              className='form-control reply-input'
              type='text'
              onChange={() =>
                setOtherReason(otherReasonInputRef.current?.value as string)
              }
              ref={otherReasonInputRef as MutableRefObject<HTMLInputElement>}
            />
          )}
        </OtherReason>
      </ReasonsContainer>
      <SendReport
        bsStyle='primary'
        disabled={!isValidReport()}
        onClick={submitReport}
      >
        Send report
      </SendReport>
    </StyledModal>
  );
}
